import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Search, FileBarChart, UserCheck, UserX,
  Clock, X, ChevronRight, BarChart3, ShieldAlert,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import EmptyState from '@/components/ux/EmptyState';
import { ViewToggle, useViewMode } from '@/components/ux/ViewToggle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

// Statuses that mean the round has been attempted and is done (has or can have a report).
// We check `effectiveStatus` (enriched by backend from real session status) first.
const TERMINAL_STATUSES = new Set(['passed', 'failed', 'completed', 'terminated', 'expired', 'no_show']);

const DECISION_COLORS = {
  hire:    'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
  reject:  'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
  on_hold: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
};

const ROUND_CHIP_COLORS = {
  passed:     'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/25 hover:bg-green-500/25',
  failed:     'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/25 hover:bg-red-500/25',
  completed:  'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/25 hover:bg-blue-500/25',
  terminated: 'bg-red-400/15 text-red-600 border-red-400/25 hover:bg-red-400/25',
  expired:    'bg-orange-500/15 text-orange-700 border-orange-500/25 hover:bg-orange-500/25',
  no_show:    'bg-gray-400/15 text-gray-600 border-gray-400/25 hover:bg-gray-400/25',
};

const STATUS_LABEL = {
  passed:     'Passed',
  failed:     'Failed',
  completed:  'Done',
  terminated: 'Terminated',
  expired:    'Expired',
  no_show:    'No Show',
};

const DECISION_FILTER_TABS = [
  { value: '',         label: 'All' },
  { value: '__active', label: 'Active' },
  { value: 'hire',     label: 'Hired' },
  { value: 'reject',   label: 'Rejected' },
  { value: 'on_hold',  label: 'On Hold' },
];

/** Return the latest attempt per roundNumber, sorted ascending. */
function latestPerRoundNumber(rounds = []) {
  const map = {};
  for (const r of rounds) {
    const rn   = r.roundNumber;
    const prev = map[rn];
    if (!prev || (r.attemptNumber || 1) >= (prev.attemptNumber || 1)) map[rn] = r;
  }
  return Object.values(map).sort((a, b) => a.roundNumber - b.roundNumber);
}

/**
 * Shared derivation for both the card and the row, so the two views can never
 * disagree about a candidate's progress or average score.
 */
function deriveProcess(process) {
  const candidate = process.candidateId;
  const template  = process.templateId;
  const name = candidate
    ? `${candidate.firstName} ${candidate.lastName}`.trim() || 'Unknown'
    : 'Unknown';

  const latestRounds = latestPerRoundNumber(process.rounds || []);

  // Use effectiveStatus (enriched from real session status) — falls back to r.status
  // for rounds not yet enriched.
  const completedRounds = latestRounds.filter(r =>
    TERMINAL_STATUSES.has(r.effectiveStatus || r.status)
  );
  const plannedTotal = process.plannedTotalRounds || latestRounds.length || 0;

  // Use overallScore (from InterviewReport, enriched by backend) first; fall back to r.score
  const scoredRounds = completedRounds.filter(r => (r.overallScore ?? r.score) != null);
  const avgScore = scoredRounds.length > 0
    ? Math.round(scoredRounds.reduce((s, r) => s + (r.overallScore ?? r.score), 0) / scoredRounds.length)
    : null;

  return { candidate, template, name, completedRounds, plannedTotal, avgScore };
}

/** Hire / reject / on-hold pill, shared by both views. */
function DecisionPill({ decision }) {
  if (decision && DECISION_COLORS[decision]) {
    return (
      <Badge variant="outline" className={cn('text-[10px]', DECISION_COLORS[decision])}>
        {decision === 'hire' ? 'Hired' : decision === 'reject' ? 'Rejected' : 'On Hold'}
      </Badge>
    );
  }
  return <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">pending</Badge>;
}

// ── Pipeline row — dense alternative to ProcessCard ───────────────────────────
function ProcessRow({ process, onViewPipeline }) {
  const { template, name, candidate, completedRounds, plannedTotal, avgScore } = deriveProcess(process);

  return (
    <div className="group grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2.5 transition-colors last:border-0 hover:bg-muted/40">
      <div className="col-span-12 min-w-0 sm:col-span-3">
        <p className="truncate text-xs font-semibold text-foreground">{name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{candidate?.email || '—'}</p>
      </div>

      <div className="col-span-6 hidden min-w-0 sm:col-span-3 sm:block">
        <p className="truncate text-[11px] text-muted-foreground">{template?.title || '—'}</p>
      </div>

      <div className="col-span-4 text-[11px] text-muted-foreground tabular-nums sm:col-span-2">
        {completedRounds.length} / {plannedTotal} rounds
      </div>

      <div className="col-span-4 sm:col-span-1">
        {avgScore != null
          ? <span className="text-xs font-bold tabular-nums text-foreground">{avgScore}%</span>
          : <span className="text-[11px] text-muted-foreground">—</span>}
      </div>

      <div className="col-span-4 sm:col-span-2">
        <DecisionPill decision={process.finalDecision} />
      </div>

      <div className="col-span-12 flex justify-end sm:col-span-1">
        <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[11px] text-primary hover:text-primary"
          onClick={() => onViewPipeline(process._id)}>
          Pipeline <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// ── Pipeline card for one candidate+JD process ────────────────────────────────
function ProcessCard({ process, onViewPipeline, onViewRoundReport }) {
  const { candidate, template, name, completedRounds, plannedTotal, avgScore } = deriveProcess(process);

  return (
    <Card className="flex flex-col hover:shadow-md transition-shadow">
      <CardContent className="p-4 flex flex-col gap-3 flex-1">

        {/* ── Candidate + decision header ─────────────────────────── */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{candidate?.email || '—'}</div>
            {template?.position && template.position !== template.title && (
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                {template.position}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <DecisionPill decision={process.finalDecision} />
            <span className="text-[10px] text-muted-foreground capitalize">
              {process.status?.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* ── JD / Template label ─────────────────────────────────── */}
        <div className="text-xs font-medium text-foreground/80 truncate border-b pb-2">
          {template?.title || '—'}
        </div>

        {/* ── Completed round chips ───────────────────────────────── */}
        <div className="flex-1">
          {completedRounds.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No completed rounds yet</p>
          ) : (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                Completed Rounds
              </div>
              <div className="flex flex-wrap gap-1.5">
                {completedRounds.map((r) => {
                  const eff       = r.effectiveStatus || r.status;
                  const chipColor = ROUND_CHIP_COLORS[eff] || 'bg-muted text-muted-foreground border-muted';
                  const score     = r.overallScore ?? r.score;
                  const hasReport = !!r.sessionId;
                  return (
                    <button
                      key={`${r.roundNumber}-${r.attemptNumber || 1}`}
                      type="button"
                      onClick={() => hasReport && onViewRoundReport(r.sessionId)}
                      disabled={!hasReport}
                      className={cn(
                        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium transition-colors',
                        chipColor,
                        hasReport ? 'cursor-pointer' : 'cursor-default opacity-70',
                      )}
                      title={hasReport ? 'View detailed report' : undefined}
                    >
                      <span>{r.roundName || `Round ${r.roundNumber}`}</span>
                      {score != null && (
                        <span className="opacity-70">· {Math.round(score)}%</span>
                      )}
                      {(r.attemptNumber || 1) > 1 && (
                        <span className="opacity-60 text-[10px]">(#{r.attemptNumber})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer — summary + pipeline link ────────────────────── */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="text-[11px] text-muted-foreground">
            <span>{completedRounds.length} / {plannedTotal} complete</span>
            {avgScore != null && (
              <span className="ml-2 font-medium text-foreground">avg {avgScore}%</span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-2 text-xs text-primary hover:text-primary"
            onClick={() => onViewPipeline(process._id)}
          >
            Pipeline <ChevronRight className="h-3 w-3" />
          </Button>
        </div>

      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const navigate = useNavigate();
  const [stats,     setStats]     = useState(null);
  const [processes, setProcesses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [search,         setSearch]         = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [viewMode,       setViewMode]       = useViewMode('echohire.reports.view');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (decisionFilter === '__active') params.status        = 'in_progress';
      else if (decisionFilter)           params.finalDecision = decisionFilter;

      const [statsRes, processesRes, templatesRes] = await Promise.all([
        apiClient.getDashboardStats(),
        apiClient.listCandidateProcesses(params),
        apiClient.getTemplates(),
      ]);
      setStats(statsRes.data);
      setProcesses(processesRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch {
      setStats(null);
      setProcesses([]);
    } finally {
      setLoading(false);
    }
  }, [decisionFilter]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter: search + template + must have ≥1 completed round
  const filtered = useMemo(() => {
    return processes.filter(p => {
      // Must have at least 1 completed round to appear here (check effectiveStatus first)
      const hasCompleted = (p.rounds || []).some(r => TERMINAL_STATUSES.has(r.effectiveStatus || r.status));
      if (!hasCompleted) return false;

      // Template filter
      if (templateFilter) {
        const tid = (p.templateId?._id || p.templateId)?.toString();
        if (tid !== templateFilter) return false;
      }

      // Full-text search
      if (search) {
        const q = search.toLowerCase();
        const cand = `${p.candidateId?.firstName || ''} ${p.candidateId?.lastName || ''} ${p.candidateId?.email || ''}`.toLowerCase();
        const tmpl = (p.templateId?.title || '').toLowerCase();
        if (!cand.includes(q) && !tmpl.includes(q)) return false;
      }

      return true;
    });
  }, [processes, search, templateFilter]);

  const hasFilters = !!(search || templateFilter || decisionFilter);

  // ── KPI data ────────────────────────────────────────────────────────────────
  const kpiTotal    = stats?.reportsCount  ?? stats?.completed ?? stats?.total ?? 0;
  const kpiHired    = stats?.accepted      ?? 0;
  const kpiRejected = stats?.rejected      ?? 0;
  const kpiAwaiting = stats?.awaitingDecision ?? stats?.pending ?? 0;
  const kpiTerm     = stats?.terminated    ?? 0;

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (loading && !stats) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-md bg-muted/60" />
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-lg bg-muted/40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Interview Reports</h1>
          <p className="text-[11px] text-muted-foreground">
            Pipeline view per candidate — completed rounds only
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={load}
          disabled={loading}
        >
          <Loader2 className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* ── KPI strip ────────────────────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
          <DashboardKpiCard compact title="Reports"    value={kpiTotal}    icon={FileBarChart} tone="primary"     />
          <DashboardKpiCard compact title="Hired"      value={kpiHired}    icon={UserCheck}    tone="success"     />
          <DashboardKpiCard compact title="Rejected"   value={kpiRejected} icon={UserX}        tone="destructive" />
          <DashboardKpiCard compact title="Awaiting"   value={kpiAwaiting} icon={Clock}        tone="warning"     />
          <DashboardKpiCard compact title="Terminated" value={kpiTerm}     icon={ShieldAlert}  tone="destructive" />
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Decision tabs */}
        <Tabs
          value={decisionFilter || 'all'}
          onValueChange={(v) => setDecisionFilter(v === 'all' ? '' : v)}
        >
          <TabsList className="h-8">
            {DECISION_FILTER_TABS.map((t) => (
              <TabsTrigger
                key={t.value || 'all'}
                value={t.value || 'all'}
                className="h-6 px-2.5 text-[11px] data-[state=active]:shadow-sm"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="relative flex-1 min-w-[10rem] max-w-xs">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search candidate or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-7 text-xs"
          />
          {search && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Template/JD filter */}
        <select
          value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}
          className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">All roles</option>
          {templates.map((t) => (
            <option key={t._id} value={t._id}>{t.title}</option>
          ))}
        </select>

        {/* Clear */}
        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => { setSearch(''); setTemplateFilter(''); setDecisionFilter(''); }}
          >
            Clear
          </Button>
        )}

        <ViewToggle value={viewMode} onChange={setViewMode} className="ml-auto" />
      </div>

      {/* ── Pipeline grid ────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-lg bg-muted/40" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No completed rounds"
          description={
            hasFilters
              ? 'No candidates match your filters. Try adjusting the search or tabs.'
              : 'Completed interview rounds will appear here, grouped by candidate and job.'
          }
          actionLabel={hasFilters ? 'Clear filters' : undefined}
          onAction={hasFilters ? () => { setSearch(''); setTemplateFilter(''); setDecisionFilter(''); } : undefined}
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {filtered.length} candidate{filtered.length !== 1 ? 's' : ''}
          </p>
          {viewMode === 'list' ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="hidden grid-cols-12 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
                <span className="col-span-3">Candidate</span>
                <span className="col-span-3">Job description</span>
                <span className="col-span-2">Progress</span>
                <span className="col-span-1">Avg</span>
                <span className="col-span-2">Decision</span>
                <span className="col-span-1" />
              </div>
              {filtered.map((p) => (
                <ProcessRow
                  key={p._id}
                  process={p}
                  onViewPipeline={(id) => navigate(`/admin/candidate-process/${id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p) => (
                <ProcessCard
                  key={p._id}
                  process={p}
                  onViewPipeline={(id) => navigate(`/admin/candidate-process/${id}`)}
                  onViewRoundReport={(sessionId) => navigate(`/admin/reports/${sessionId}`)}
                />
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
